<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { DEFAULT_ORDER_OPTION } from '@/shared/config';
  import { TableHeaderSection, DefaultTable, SearchNoResults } from '@/shared/ui';
  import DepartmentsTableRow from './DepartmentsTableRow.vue';
  import type { Department } from '@/entities/Department';
  import { OrderOptions } from '@/shared/lib';

  const props = withDefaults(
    defineProps<{
      orderOptions?: OrderOptions[];
      departments?: Department[];
      loading?: boolean;
      isFilterActive?: boolean;
      searchTerm?: string;
      selectedCount: number;
      hasSelected: boolean;
      isSelected: (id: number) => boolean;
      selectedItems: number[];
      allSelected: boolean;
      onToggleSelection: (id: number) => void;
      onToggleAll: () => void;
      onOpenDeleteConfirmation?: () => Promise<void>;
      onShowNoSelectionError?: () => void;
    }>(),
    {
      orderOptions: () => [],
      departments: () => [],
      loading: false,
      isFilterActive: false,
      selectedCount: 0,
      hasSelected: false,
      isSelected: () => false,
      selectedItems: () => [],
      allSelected: false,
      onToggleSelection: () => {},
      onToggleAll: () => {},
      onOpenDeleteConfirmation: async () => {},
      onShowNoSelectionError: () => {},
      searchTerm: '',
    }
  );

  const expandedDepartmentId = ref<number | null>(null);

  const handleToggleExpand = (departmentId: number) => {
    expandedDepartmentId.value = expandedDepartmentId.value === departmentId ? null : departmentId;
  };

  defineExpose({
    handleToggleExpand,
    expandedDepartmentId,
  });

  const emit = defineEmits<{
    'update:sort': [sort: string, order: string];
    'update:filter': [filters: Record<string, unknown>];
    'filter-clicked': [];
    edit: [id: number];
    copy: [id: number];
    delete: [id: number];
    'bulk-delete': [ids: number[]];
  }>();

  const handleToggleSelection = (id: number) => {
    props.onToggleSelection(id);
  };

  const handleToggleAll = () => {
    props.onToggleAll();
  };

  const handleBulkDelete = async () => {
    if (props.selectedItems.length === 0) {
      props.onShowNoSelectionError();
      return;
    }
    await props.onOpenDeleteConfirmation();
  };

  const handleSortOptions = (sortCamp: string, orderFlow: string) => {
    emit('update:sort', sortCamp, orderFlow);
  };

  const handleFilter = (filters: Record<string, unknown>) => {
    emit('update:filter', filters);
  };

  const handleFilterClicked = () => {
    emit('filter-clicked');
  };

  const handleEdit = (id: number) => {
    emit('edit', id);
  };

  const handleCopy = (id: number) => {
    emit('copy', id);
  };

  const handleDelete = (id: number) => {
    emit('delete', id);
  };

  const defaultOrderOptions: OrderOptions[] = [
    { ...DEFAULT_ORDER_OPTION, label: 'Por departamento mais recente' },
    { label: 'Por departamento mais antigo', sort: 'createdAt', order: 'asc' },
    { label: 'Ordem alfabética crescente', sort: 'code', order: 'asc' },
    { label: 'Ordem alfabética decrescente', sort: 'code', order: 'desc' },
  ];

  const orderOptions = computed(() =>
    props.orderOptions.length > 0 ? props.orderOptions : defaultOrderOptions
  );

  const shouldShowNoResults = computed(
    () =>
      !props.loading &&
      props.departments.length === 0 &&
      (!!props.searchTerm || props.isFilterActive)
  );
</script>

<template>
  <DefaultTable>
    <TableHeaderSection
      :headers="['Departamentos', 'Ações', '']"
      :item-count="departments.length"
      item-type="departamentos"
      :has-selected="hasSelected"
      :selected-count="selectedCount"
      :selected-documents="selectedItems"
      :all-selected="allSelected"
      :show-column-headers="departments.length > 0"
      :order-options="orderOptions"
      :is-filter-active="isFilterActive"
      :has-filter="false"
      :delete-disabled="!hasSelected"
      custom-message="Lista de Departamentos"
      :show-item-count="false"
      :th-class="'pl-6 py-4.5 text-left text-xs font-semibold text-gray-07 w-min whitespace-nowrap align-middle'"
      th-checkbox-class="w-20 align-middle text-center"
      @toggle-all="handleToggleAll"
      @update:sort="handleSortOptions"
      @update:filter="handleFilter"
      @filter-clicked="handleFilterClicked"
      @delete="handleBulkDelete"
    />
    <tbody class="bg-white-01 divide-y divide-gray-02 border-t border-gray-02">
      <DepartmentsTableRow
        v-for="department in departments"
        :key="department.id"
        :department="department"
        :is-expanded="expandedDepartmentId === department.id"
        :is-selected="props.isSelected(department.id)"
        @toggle-selection="handleToggleSelection"
        @toggle-expand="handleToggleExpand(department.id)"
        @edit="handleEdit"
        @copy="handleCopy"
        @delete="handleDelete"
      />
      <tr v-if="shouldShowNoResults">
        <td colspan="100">
          <SearchNoResults />
        </td>
      </tr>
    </tbody>
  </DefaultTable>
</template>
